import { 
  Container, 
  Grid, 
  useMediaQuery, 
  useTheme, 
  Button 
} from "@mui/material";
import _ from 'lodash';
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  Cell, 
  Pie,
  PieChart, 
  PolarAngleAxis, 
  PolarGrid, 
  PolarRadiusAxis, 
  Radar, 
  RadarChart, 
  ResponsiveContainer, 
  Tooltip,
  Text
} from "recharts";
import styled from "styled-components";
// import domtoimage from "dom-to-image";
// import * as htmlToImage from 'html-to-image';
import { getColors } from "../resources/colors";
import { GridCard } from "../StyledComponents";
import { AllMissionDataType } from "../types/MissionData";
import { 
  domains, 
  DomainType 
} from '../types/MissionType';
import { UserProfile } from "../types/UserProfile";
import DomainScatterChartTabs from "./DomainScatterChartTabs";
import DownloadIcon from '@mui/icons-material/Download';
// import { generate, Template, Font } from "@pdfme/generator";
// import Vertical from '../pdf/Vertical.json'
// import tick from '../image/tick.png'
import { report } from "process";
// import PreviewGameResults from "./PreviewGameResults";


// import chinese from "../fonts/chinese.ttf"

const COLORS = [['#ff3333', '#f9f9f9'], ['#9966cc', '#f9f9f9'], ['#66cc33', '#f9f9f9'], ['#ffcc33', '#f9f9f9'], ['#33ccff', '#f9f9f9'], ['#ff6699', '#f9f9f9']];

const getRandScore = (max = 99, min = 80): number => {
    return _.round((Math.random() * (max - min)) + min, 2);
};

export enum ScoreLevel {
    first = "1",
    second = "2",
    third = "3",
    forth = "4",
}

export enum ScoreLevelColor {
    first = "green",
    second = "#F9DDA4",
    third = "orange",
    forth = "red"
}
const StyledGridHeader3 = styled.div`
    padding-bottom: 10px;
    font-size: 14px;
    text-align: center;
    margin-left: -20px
`

// const StyledGridRow2 = styled(Grid)`
//     padding-top: 5px;
//     padding-bottom: 40px;
//     padding-left: 40px;
//     padding-right: 40px;
// `

// const getScoreLevelColor = (scoreLevel: ScoreLevel): string => {
//     if (scoreLevel === ScoreLevel.first) {
//         return ScoreLevelColor.first;
//     } else if (scoreLevel === ScoreLevel.second) {
//         return ScoreLevelColor.second;
//     } else if (scoreLevel === ScoreLevel.third) {
//         return ScoreLevelColor.third;
//     } else {
//         return ScoreLevelColor.forth;
//     }
// }
// const getScoreLevel = (score: number): ScoreLevel => {
//     if (score > 85) {
//         return ScoreLevel.first;
//     } else if (score > 75) {
//         return ScoreLevel.second;
//     } else if (score > 70) {
//         return ScoreLevel.third;
//     } else {
//         return ScoreLevel.forth;
//     }
// }

interface MissionTabProps {
    domainScore: { "domain": string, "chineseDomain": string, "score": number }[];
    missionData: AllMissionDataType[];
    selectedUser: UserProfile;
    userScore: number
}

const RenderPieChart = React.memo((props: { domainScore: { "domain": string, "chineseDomain": string, "score": number }[] }) => {
    let pieChartData: { [score: string]: number }[] = [];
    let obj: { "domain": string, "chineseDomain": string, "score": number } | undefined = { "domain": "", "chineseDomain": "", "score": 0 };
    let point: number = 0;
    return (
      <Grid container spacing={0} style={{ display: "flex", alignItems: "center", marginTop: 20, marginBottom: 20 }} >
        {domains.map((domain, index) => {
          if (props.domainScore !== undefined) {
            obj = props.domainScore.find(x => x.domain === domain);
            pieChartData = toPieChartData(domain, props.domainScore);
            point = pieChartData[0].score;
          }
          return (
            <Grid key={`Grid-${index}`} item lg={2} md={4} sm={6} xs={6}>
              <ResponsiveContainer width="90%" height={140}>
                <PieChart>
                  <Pie data={pieChartData} dataKey="score" innerRadius={40} outerRadius={50} animationBegin={400} startAngle={90} endAngle={450}>
                    {pieChartData.map((entry, _index) => <Cell key={`PieCell-${index}`} fill={COLORS[index][_index]} />)}
                  </Pie>
                  <text x='50%' y={70} textAnchor='middle' dominantBaseline='middle'>
                    {point.toFixed(0) + '%'}
                  </text>
                </PieChart>
              </ResponsiveContainer>
              <StyledGridHeader3>{(obj !== undefined) ? obj.chineseDomain : NaN}</StyledGridHeader3>
            </Grid>
          );
        })}</Grid>)
});
  
function toPieChartData(domain: DomainType, scores: { "domain": string, "chineseDomain": string, "score": number }[]): { [score: string]: number }[] {
  let data: { [score: string]: number }[] = [];
  const obj = scores.find(x => x.domain === domain);
  if (obj !== undefined) {
    if (obj.score > 100)
      obj.score = 100;
    data = [{
      "score": obj.score
    },
    {
      "score": 100 - obj.score
    }]
  }
  return data;
}

export default function MissionTab(props: MissionTabProps) {

  const [missionDistributionPieChartData, setMissionDistributionPieChartData] = useState<{ name: string, count: number }[]>();
  const {t} = useTranslation();
  const theme = useTheme();
  const smBreakPoint = useMediaQuery(theme.breakpoints.up('sm'));

  let combinedHistory1 = [
    { score: getRandScore(95, 90), time: -5 },
    { score: getRandScore(90, 88), time: -4 },
    { score: getRandScore(89, 87), time: -3 },
    { score: getRandScore(88, 82), time: -2 },
    { score: getRandScore(83, 79), time: -1 },
    { score: getRandScore(90, 65), time: 0 }
  ];

  useEffect(() => {
    if (props.missionData) {
      const pieChartReducer = props.missionData.reduce<{ [key: string]: { name: string, count: number } }>((buffer, entry) => {
        // Recharts bugged and labelFormatter property not working to translate mission key to chinese, so I use chinese name as key to improve UX.
        const chineseKey = t(`gameName.${entry.key}` as unknown as TemplateStringsArray);
        (!buffer[chineseKey] && (buffer[chineseKey] = { name: chineseKey, count: 0 }));
        buffer[chineseKey].count++;
        return buffer;
      }, {});

      setMissionDistributionPieChartData(Object.values(pieChartReducer));
    }
  }, [props.missionData]);

  const getMostPlayedMission = (): string => {
    const maxEntry = _(missionDistributionPieChartData).maxBy(entry => entry.count);
    return `${maxEntry?.name}：${maxEntry?.count} ${t("common.times")}`;
  }

  const getBestPerformanceDoamin = (): string => {
    const maxDomain = _(props.domainScore).maxBy(entry => entry.score);
    return `${maxDomain!.chineseDomain}：${Math.round(maxDomain!.score)} ${t("common.score")}`;
  }

  const mapMissionDistributionPieChartData = (missionDistributionPieChartData: { name: string, count: number }[]) => {
    const colors = getColors(missionDistributionPieChartData.length);
    return missionDistributionPieChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={colors[index]} />)
  }

  const getLevelByScore = (score: number): { level: number, next: number } => {
    if (!score) return { level: 0, next: 10 };

    const currentLevel = Math.floor(Math.pow((score - 10), 0.8) / 15);
    const nextLevelExp = Math.round(10 + Math.pow(((currentLevel + 1) * 15), 1.25))

    return { level: currentLevel, next: nextLevelExp - score };
  }

  // const print_by_id = () => {
  //   const elem = document.getElementById('user_level');
  //   if (! elem) return;

  //   const font: Font = {
  //     chinese: {
  //       data: fetch('../fonts/chinese.ttf').then((res) => res.arrayBuffer()),
  //       fallback: true,
  //     }
  //   }

  //   const template: Template = Vertical
  //   const report_type: string = `${t('new_report_pages.game_result')}${t('common.report')}`
  //   const name: string = `${t('common.name')}: ${props.selectedUser.name}`
  //   const gender: string = `${t('common.gender')}: ${props.selectedUser.gender}`
  //   // const age: string = `${t('common.age')}：${{props.selectedUser.year_of_birth}}`
  //   const inputs = [{
  //     "Report_type": report_type,
  //     "Name": name,
  //     "Gender": gender,
  //     "Age": "age",
  //     "Level": tick,
  //     "Preference": tick,
  //     "Performance": tick,
  //     "Summary_of_preformance": tick,
  //     "Performance_trends": tick
  //   }]

  //   generate({ template, inputs, options: { font } }).then((pdf) => {
  //     console.log(pdf)

  //     const blob = new Blob([pdf.buffer], { type: "application/pdf" });
  //     const a = document.createElementNS("http://www.w3.org/1999/xhtml", "a");
  //     a.setAttribute("download", Date.now().toString());
  //     a.setAttribute("rel", "noopener");
  //     a.setAttribute("href", URL.createObjectURL(blob));
  //     a.click();
  //     // window.open(URL.createObjectURL(blob))?.print();
  //   })
  // }

  return (
    <Container style={{ minHeight: '100px', paddingBottom: 20, marginTop: 40 }}>
      {/* <Grid item sx={{ paddingBottom: 4 }}>
        <Button onClick={() => handleOpenPreviewDialog()} variant="contained" endIcon={<DownloadIcon />}  >
          {t("common.download")}{t("new_report_pages.game_result")}{t("common.report")}
        </Button>
      </Grid>         */}
      <Grid container spacing={4} id='info'>
        <Grid item lg={4} md={12} sm={12} xs={12} id='user_level'>
          <GridCard title={t("missionTab.title_user_level")} desc={t("missionTab.desc_user_level")}>
            <div style={{ minHeight: 340, marginTop: 50 }}>
              <Text style={{ fontSize: 36 }}>
                {"Lv. "}
              </Text>
              <Text style={{ fontSize: 120 }}>
                {getLevelByScore(props.userScore).level}
              </Text>
              <br />
              <br />
              <Text style={{ fontSize: 20 }}>
                {`${t("missionTab.text_total_exp")}${props.userScore? props.userScore : 0}`}
              </Text>
              <br />
              <Text style={{ fontSize: 20 }}>
                {t("missionTab.text_next_level")+getLevelByScore(props.userScore).next}
              </Text>
            </div>
          </GridCard>
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} id='user_preference'>
          <GridCard title={t("missionTab.title_user_preference")} desc={t("missionTab.desc_user_preference")}>
            {
              (missionDistributionPieChartData && missionDistributionPieChartData.length !== 0) ?
              (
                <>
                  <div style={{ fontSize: 17, marginTop: 20}}>
                    {t("missionTab.text_mission_play_most")}
                    <br/>
                    <div style={{ fontSize: 22 }}>
                      {
                        getMostPlayedMission()
                      }
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Tooltip labelFormatter={(v: number) => `${v}`} />
                      {/* <Tooltip labelFormatter={(v: number) => `${v} ${t("common.times")} (${Math.round((v as number) / props.missionData.length * 100)}%)`} /> */}
                      <Pie dataKey="count" data={missionDistributionPieChartData} outerRadius={100} animationBegin={400} innerRadius={60}>
                        {mapMissionDistributionPieChartData(missionDistributionPieChartData)}
                      </Pie>
                      <text x="50%" y={155} textAnchor="middle" dominantBaseline="middle" fontSize={16}>
                        {props.missionData.length + " " + t("common.times")}
                      </text>
                    </PieChart>
                  </ResponsiveContainer>
                </>
              )
              :
              (
                <div style={{ fontSize: 64, marginTop: 110, minHeight: 280, textAlign: "center" }}>
                  {t("no__.record")}
                  <p style={{ fontSize: 16, textAlign: "center" }}>
                    {t("common.start_a_game")}
                  </p>
                </div>
              )
            }
          </GridCard>
        </Grid>
        <Grid item lg={4} md={6} sm={12} xs={12} id='domain_radar'>
          <GridCard title={t("missionTab.title_domain_radar")} desc={t("missionTab.desc_domain_radar")}>
            {
              Object.values(props.domainScore).every(domain => domain.score === 0) ? 
              (
                <div style={{ fontSize: 64, marginTop: 110, minHeight: 280, textAlign: "center" }}>
                  {t("no__.record")}
                  <p style={{ fontSize: 16, textAlign: "center" }}>
                    {t("common.start_a_game")}
                  </p>
                </div>
              )
              :
              (
                <>
                  <div style={{ fontSize: 17, marginTop: 20 }}>
                    {t("missionTab.text_best_domain")}<br />
                    <div style={{ fontSize: 22 }}>
                      {props.domainScore ?
                        getBestPerformanceDoamin() :
                        t("no__.data")
                      }
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={320} id='radarChart'>
                    <RadarChart outerRadius={100} height={300} data={props.domainScore.map(dScore => { return { ...dScore, score: Math.round(dScore.score) } })}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="chineseDomain" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      <Radar name={props.selectedUser.name} dataKey="score" stroke="#6cb6d9" fill="#79ccf2" fillOpacity={0.6} />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </>
              )
            }
          </GridCard>
        </Grid>
        <Grid item sm={12} xs={12} id='domain_overview'>
          <GridCard title={t("missionTab.title_domain_overview")} desc={t("missionTab.desc_domain_overview")}>
            {
              Object.values(props.domainScore).every(domain => domain.score === 0) ?
              (
                <>
                  <h3>
                    {t("no__.record")}
                  </h3>
                  <p>
                    {t("common.start_a_game")}
                  </p>
                </>
              )
              :
              (
                <RenderPieChart domainScore={props.domainScore} />
              )
            }
          </GridCard>
        </Grid>
        {props.missionData && <DomainScatterChartTabs data={props.missionData} />}
      </Grid >
      {/* <PreviewGameResults 
        isOpen={openPreviewDialog} 
        onClose={()=>setOpenPreviewDialog(false)} 
        userProfile={props.selectedUser}
        gameData={missionDistributionPieChartData} 
        lv={getLevelByScore(props.userScore).level}
        userScore={props.userScore? props.userScore : 0}
        getMostPlayed={getMostPlayedMission()}
        bestPerformance={getBestPerformanceDoamin()}
      /> */}
    </Container>
  )
}